<template>
  <div class="detail-page px-4 py-3">
    <div v-for="(section, index) in sections" :key="section.key">
      <div
        class="gate-view pb-3 flex justify-between items-center"
        @click="toggleSection(section.key)"
      >
        <span>
          <van-icon v-if="activeSections.includes(section.key)" name="arrow-down" />
          <van-icon v-else name="arrow" />
          {{ section.title }}
        </span>
        <div v-if="index === 0 && !isDoorSummaryView" class="edit-link" @click.stop="openEdit">
          <van-icon name="edit" size="16" color="#7f7f7f" />
          <span class="ml-1">Edit</span>
        </div>
      </div>
      <transition name="collapse">
        <div v-show="activeSections.includes(section.key)" class="group-grid pb-6">
          <div
            v-for="f in fieldGroups[section.key]"
            :key="f.key"
            :class="['field-item', { 'col-span-2': f.fullRow || span2Keys.has(f.key) }]"
          >
            <div class="field-label">{{ getLabel(f.key) }}</div>
            <template v-if="(f.type || 'input') === 'input'">
              <input class="field-input" :value="displayValue(f.key)" disabled />
            </template>
            <template v-else>
              <textarea class="field-textarea" :value="displayValue(f.key)" disabled />
            </template>
          </div>
        </div>
      </transition>
    </div>
  </div>
  <van-dialog
    v-model:show="showEdit"
    :style="{
      maxHeight: 'calc(100vh - 250px)',
      overflowY: 'auto',
      width: '92vw',
      maxWidth: '680px',
    }"
    :title="!editItems || editItems.length === 0 ? 'No editable fields' : 'Detail Information'"
    :confirm-button-text="editItems && editItems.length ? '' : 'Confirm'"
    :show-cancel-button="false"
    :show-confirm-button="!editItems || editItems.length === 0"
    :before-close="handleBeforeClose"
    @confirm="onEditSave"
  >
    <div class="edit-form">
      <div
        v-for="item in editItems"
        :key="item.key"
        v-show="
          !item.visibleWhen ||
          String(editForm[item.visibleWhen.key] ?? '') === item.visibleWhen.equals
        "
        :class="['edit-row', { 'col-span-2': item.key === 'speedUpComment' }]"
      >
        <div class="edit-label">
          <span v-if="item.required" class="req">*</span>{{ getLabel(item.key) }}
        </div>
        <template v-if="item.type === 'radio'">
          <van-radio-group v-model="editForm[item.key]" direction="horizontal">
            <van-radio :name="true">Yes</van-radio>
            <van-radio :name="false">No</van-radio>
          </van-radio-group>
        </template>
        <template v-else-if="item.type === 'select' && item.key === 'account'">
          <van-field
            v-model="editForm[item.key]"
            is-link
            arrow-direction="down"
            readonly
            :placeholder="'please select'"
            @click="openAccountSelect"
          />
        </template>
        <template v-else-if="item.type === 'select'">
          <van-field
            v-model="editForm[item.key]"
            is-link
            arrow-direction="down"
            readonly
            :placeholder="'please select'"
            @click="openSelect(item.key)"
          />
        </template>
        <template v-else-if="item.type === 'textarea'">
          <textarea class="edit-textarea" v-model="editForm[item.key]" />
        </template>
        <template v-else-if="item.type === 'input'">
          <input class="edit-input" v-model="editForm[item.key]" />
        </template>
        <template v-else>
          <input class="edit-input" v-model="editForm[item.key]" />
        </template>
      </div>
    </div>
    <div v-if="editItems && editItems.length" class="dialog-footer-fixed">
      <van-button plain class="dialog-cancel" @click="showEdit = false">Cancel</van-button>
      <van-button type="primary" class="dialog-confirm" @click="onEditSave">Save</van-button>
    </div>
  </van-dialog>

  <van-popup v-if="selectKey !== 'account'" v-model:show="selectVisible" position="bottom" round>
    <van-search
      v-if="selectKey === 'fixtureType'"
      v-model="selectSearch"
      placeholder="search"
      shape="round"
    />
    <van-picker
      :columns="selectOptions"
      @confirm="onSelectConfirm"
      @cancel="selectVisible = false"
    />
  </van-popup>

  <AccountTreeSelectPopup
    v-model:show="accountTreeVisible"
    :account="editForm.account"
    :account-group="String(detailData?.accountGroup || '')"
    :account-id="editForm.accountId"
    @confirm="onAccountConfirm"
  />
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import {
  detailFieldGroups,
  pureColumns,
  detailEditConfig,
  detailSelectOptions,
  type EditConfigItem,
} from '../../utils/detail-config'
import { formatDate } from '@/utils/utils'
import { showToast } from 'vant'
import { withLoading } from '@/utils/request'
import { getClcDoor, updateClcDoor, getConfigList, getConceptList } from '@/api/detail'
import AccountTreeSelectPopup from '@/components/AccountTreeSelectPopup.vue'

const props = defineProps<{ detailData: any }>()
const emit = defineEmits(['update:detailData', 'refresh'])

const detailData = computed({
  get: () => props.detailData,
  set: (val) => emit('update:detailData', val),
})

const currentNode = computed(() => {
  const name = currentTaskName.value.trim()
  const m = name.match(/^[A-Za-z]+\d+(?:\.\d+)?/)
  return m ? m[0].toUpperCase() : ''
})

// const fieldGroups = detailFieldGroups
const fieldGroups = computed(() => {
  const node = currentNode.value
  const result: Record<string, any[]> = {}
  for (const key of Object.keys(detailFieldGroups) as Array<keyof typeof detailFieldGroups>) {
    result[key as string] = detailFieldGroups[key].filter((f) => {
      if (!node) return true
      return !f.nodes || f.nodes.length === 0 || f.nodes.includes(node)
    })
  }
  return result
})
const labelsByKey = new Map<string, string>(pureColumns.map((c) => [c.key, c.label]))
const span2Keys = new Set<string>([])
const clcDoorCode = computed(() => String(route.query.clcDoorCode || ''))
const taskId = computed(() => String(route.query.taskId || ''))

const route = useRoute()
const isDoorSummaryView = computed(() => String(route.query.fromDoorSummary || '') === '1')
const currentTaskName = computed(() => String(route.query.currentTaskName || ''))
const gateKey = computed(() => {
  const name = currentTaskName.value
  for (const key of Object.keys(detailEditConfig)) {
    if (name.startsWith(key)) return key
  }
  return ''
})

const showEdit = ref(false)
const editItems = computed(() => (gateKey.value ? detailEditConfig[gateKey.value] : []))
const editForm = ref<Record<string, any>>({})

const dynamicSelectOptions = ref<Record<string, string[]>>({})
const getSelectOptions = (key: string) =>
  dynamicSelectOptions.value[key] ?? detailSelectOptions[key] ?? []

const selectVisible = ref(false)
const accountTreeVisible = ref(false)
const selectKey = ref('')
const selectSearch = ref('')
const selectOptions = computed(() => {
  const src = getSelectOptions(selectKey.value)
  if (
    selectKey.value === 'doorConcept' &&
    (gateKey.value === 'N10.2' || gateKey.value === 'N10.3')
  ) {
    const cur = String(detailData.value?.doorConcept || '')
    const allowed = ['NIKE SPORT-S', 'NIKE SPORT-S MCE']
    // 当N10.2或N10.3时，doorConcept只允许NIKE SPORT-S和NIKE SPORT-S MCE
    if (allowed.includes(cur)) {
      return allowed.map((opt) => ({ text: opt, value: opt }))
    }
  }
  if (selectKey.value === 'fixtureType') {
    const q = selectSearch.value.trim().toLowerCase()
    const list = q ? src.filter((s) => String(s).toLowerCase().includes(q)) : src
    return list.map((opt) => ({ text: opt, value: opt }))
  }
  return src.map((opt) => ({ text: opt, value: opt }))
})
const openSelect = (key: string) => {
  if (key === 'account') {
    openAccountSelect()
    return
  }
  selectKey.value = key
  if (key === 'fixtureType') selectSearch.value = ''
  selectVisible.value = true
}

const openAccountSelect = () => {
  accountTreeVisible.value = true
}

const onAccountConfirm = (payload: { account: string; accountId: string | number }) => {
  editForm.value.account = payload.account
  editForm.value.accountId = payload.accountId
}

const onSelectConfirm = ({ selectedValues }: { selectedValues: string[] }) => {
  selectVisible.value = false
  const val = selectedValues[0]
  editForm.value[selectKey.value] = val
  if (selectKey.value === 'franchise' && val !== 'Franchise') {
    editForm.value.franchisePartner = ''
  }
}

const loadFixtureTypeOptions = async () => {
  try {
    const res: any = await getConfigList('fixture_type')
    const data = Array.isArray(res?.data?.items) ? res.data.items : []
    const list =
      typeof data[0] === 'string' ? data : data.map((it: any) => it?.value).filter((v: any) => v)
    dynamicSelectOptions.value['fixtureType'] = list
  } catch (e) {}
}

const loadPgmOwnerOptions = async () => {
  try {
    const res: any = await getConfigList('clc_master', 'pgm owner')
    const items = Array.isArray(res?.data?.items) ? res.data.items : []
    let list: string[] = []
    if (items.length > 0) {
      const val = items[0].value
      if (typeof val === 'string' && val.includes('/')) {
        list = val
          .split('/')
          .map((s) => s.trim())
          .filter(Boolean)
      } else if (typeof val === 'string' && val) {
        list = [val]
      }
    }
    dynamicSelectOptions.value['pgmOwner'] = list
  } catch (e) {}
}

const loadConceptOptions = async () => {
  try {
    const res: any = await getConceptList()
    const items = Array.isArray(res?.data?.items) ? res.data.items : []
    const prev = Array.from(
      new Set(
        items
          .filter((it: any) => String(it?.concept || '') !== 'Other')
          .map((it: any) => String(it?.concept || '')).filter(Boolean),
      ),
    )
    const door = Array.from(
      new Set(
        items
          .filter((it: any) => String(it?.isPrevious || '') !== '1')
          .filter((it: any) => String(it?.concept || '') !== 'Other')
          .filter((it: any) => String(it?.status || '') !== 'N')
          .map((it: any) => String(it?.concept || '')).filter(Boolean),
      ),
    )
    dynamicSelectOptions.value['previousConcept'] = prev as string[]
    dynamicSelectOptions.value['doorConcept'] = door as string[]
  } catch (e) {}
}

onMounted(() => {
  loadFixtureTypeOptions()
  loadPgmOwnerOptions()
  loadConceptOptions()
})

const openEdit = () => {
  editForm.value = {}
  if (editItems.value) {
    for (const it of editItems.value) {
      const v = detailData.value?.[it.key]
      if (it.type === 'radio') {
        editForm.value[it.key] = typeof v === 'boolean' ? v : false
      } else if (it.key === 'account') {
        editForm.value.account = v ?? ''
        editForm.value.accountId = detailData.value?.accountId ?? ''
      } else {
        editForm.value[it.key] = v ?? ''
      }
    }
  }
  showEdit.value = true
}

defineExpose({ openEdit })

const onEditSave = async () => {
  if (!editItems.value || editItems.value.length === 0) {
    showEdit.value = false
    return
  }
  const mergedData = { ...detailData.value, ...editForm.value }
  const requiredErrors = validateRequired(mergedData)
  if (requiredErrors.length) {
    showToast(
      `Please complete required fields: ${requiredErrors.map((k) => getLabel(k)).join(', ')}`,
    )
    showEdit.value = true
    return
  }

  try {
    await withLoading(
      async () => {
        await updateClcDoor(mergedData)
        const res = await getClcDoor(clcDoorCode.value, taskId.value)
        detailData.value = { ...detailData.value, ...(res?.data ?? mergedData) }
      },
      { message: 'Saving...', successMsg: 'Saved successfully' },
    )
    emit('refresh')
    showEdit.value = false
  } catch (error: any) {
    console.error(error)
  }
}

const isEditRowActive = (it: EditConfigItem, mergedData: Record<string, any>) => {
  if (!it.visibleWhen) return true
  const cur = mergedData[it.visibleWhen.key]
  return String(cur ?? '') === it.visibleWhen.equals
}

const validateRequired = (mergedData: Record<string, any>) => {
  const errs: string[] = []
  for (const it of editItems.value || []) {
    if (!isEditRowActive(it, mergedData)) continue
    if (it.required) {
      const val = mergedData[it.key]
      const isEmptyStr = typeof val === 'string' && val.trim() === ''
      const isMissing = val === undefined || val === null || isEmptyStr
      if (it.type === 'radio') {
        if (!(val === true || val === false)) errs.push(it.key)
      } else if (isMissing) {
        errs.push(it.key)
      }
    }
  }
  return errs
}

const handleBeforeClose = (action: string) => {
  if (action !== 'confirm') return true
  const mergedData = { ...detailData.value, ...editForm.value }
  const errs = validateRequired(mergedData)
  if (errs.length) {
    showToast(`Please complete required fields: ${errs.map((k) => getLabel(k)).join(', ')}`)
    return false
  }
  return true
}

// Sections configuration
const sections = [
  { key: 'BasicInformation', title: 'Basic Information' },
  { key: 'Location', title: 'Location' },
  { key: 'Landlord', title: 'Landlord' },
  { key: 'Space', title: 'Space' },
] as const

const activeSections = ref<string[]>(['BasicInformation', 'Location', 'Landlord', 'Space'])

const toggleSection = (key: string) => {
  if (activeSections.value.includes(key)) {
    activeSections.value = activeSections.value.filter((k) => k !== key)
  } else {
    activeSections.value.push(key)
  }
}

/** 分割千分位 */
const formatThousands = (val: any) => {
  const num = typeof val === 'number' ? val : Number(String(val).replace(/,/g, '').trim())
  if (!Number.isFinite(num)) return String(val)
  return new Intl.NumberFormat('en-US').format(num)
}

const displayValue = (key: string) => {
  const v = detailData.value?.[key]
  if (v === null || v === undefined) return ''

  switch (key) {
    case 'executionFlow':
      return formatDate(v)
    case 'revFcst':
      return formatThousands(v)
    default:
      return String(v)
  }
}

const getLabel = (key: string) => labelsByKey.get(key) ?? key
</script>

<style scoped lang="scss">
.detail-page {
  background-color: #fff;
  min-height: 100vh;
}

@media (orientation: landscape) {
  .detail-page {
    zoom: 0.5;
  }
}

.gate-view {
  font-size: 14px;
  font-weight: bold;
}

.group-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}
.field-item {
  display: flex;
  flex-direction: column;
}
.field-label {
  font-size: 12px;
  color: rgb(127, 127, 127);
  margin-bottom: 4px;
}

.field-input,
.field-textarea,
.edit-input,
.edit-select,
.edit-textarea {
  color: rgba(0, 0, 0, 0.65);
  border-radius: 6px;
  padding: 5px;
  font-size: 13px;
  border: none;
  width: 100%;
}
.field-input,
.field-textarea {
  background-color: #dfdfdf;
}

.field-input:disabled,
.field-textarea:disabled {
  opacity: 1;
}
.field-textarea {
  min-height: 64px;
  resize: none;
}
.col-span-2 {
  grid-column: span 2;
}

/* Transition styles from index.vue */
.collapse-enter-from,
.collapse-leave-to {
  height: 0;
  opacity: 0;
}
.collapse-enter-to,
.collapse-leave-from {
  height: auto;
  opacity: 1;
}
.collapse-enter-active,
.collapse-leave-active {
  overflow: hidden;
  transition:
    height 0.25s ease,
    opacity 0.2s ease;
}

.edit-link {
  display: inline-flex;
  align-items: center;
  color: #7f7f7f;
  font-size: 14px;
  cursor: pointer;
}
.edit-link:hover {
  color: #595959;
}
.edit-form {
  padding: 4px 8px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}
.dialog-footer-fixed {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px;
  background: #fff;
  border-top: 1px solid #ebedf0;
}
.dialog-confirm,
.dialog-cancel {
  height: 40px;
  min-width: 80px;
}
.dialog-confirm {
  background-color: #6e010f;
  border: none;
}
.edit-row {
  margin-bottom: 0;
}
.edit-label {
  font-size: 12px;
  color: #7f7f7f;
  margin-bottom: 4px;
}
.req {
  color: #ee0a24;
  margin-right: 2px;
}
.edit-input {
  width: 100%;
  border: 1px solid #ebedf0;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 13px;
}
.edit-select {
  width: 100%;
  border: 1px solid #ebedf0;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 13px;
  background-color: #fff;
}
.edit-textarea {
  width: 100%;
  min-height: 72px;
  border: 1px solid #ebedf0;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 13px;
  resize: none;
}
</style>
